import React, { useState, useEffect, useCallback } from "react";
import { Grid, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import SmsTemplateCard from "../components/sms/SmsTemplateCard";
import CreateSmsTemplateModal from "../components/sms/CreateSmsTemplateModal";
import UpdateSmsTemplateModal from "../components/sms/UpdateSmsTemplateModal";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSmsTemplates,
  deleteSmsTemplate,
  resetSmsTemplateServerStatus,
} from "../redux/actions/smsTemplateActions";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";
import { SmsTemplateResponseDto } from "../shared/dtos/sms/SmsTemplateResponseDto";

const SmsTemplatesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: smsTemplates,
    loadingFetchAll: loadingSmsTemplates,
    errorFetchAll: errorLoadingSmsTemplates,
    serverStatus,
    loadingDelete: loadingSmsTemplateDelete,
    loadingCreate: loadingSmsTemplateCreate,
    loadingUpdate: loadingSmsTemplateUpdate,
    errorCreate: errorCreatingSmsTemplate,
    errorUpdate: errorUpdatingSmsTemplate,
  } = useAppSelector((state) => state.smsTemplate);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SmsTemplateResponseDto | null>(null);

  const canCreate = hasPermission("sms_template:create");
  const canUpdate = hasPermission("sms_template:update");
  const canDelete = hasPermission("sms_template:delete");

  useEffect(() => {
    dispatch(getSmsTemplates());
  }, [dispatch]);

  const handleCloseCreate = useCallback(
    () => !loadingSmsTemplateCreate && setIsCreateModalOpen(false),
    [loadingSmsTemplateCreate],
  );
  const handleCloseUpdate = useCallback(
    () =>
      !loadingSmsTemplateUpdate &&
      (setIsUpdateModalOpen(false), setSelectedTemplate(null)),
    [loadingSmsTemplateUpdate],
  );
  const handleCloseDelete = useCallback(
    () =>
      !loadingSmsTemplateDelete &&
      (setIsDeleteModalOpen(false), setSelectedTemplate(null)),
    [loadingSmsTemplateDelete],
  );

  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.operation === "create" && serverStatus.status === 201)
      handleCloseCreate();
    if (serverStatus.operation === "update" && serverStatus.status === 200)
      handleCloseUpdate();
    if (serverStatus.operation === "delete" && serverStatus.status === 200)
      handleCloseDelete();
    dispatch(resetSmsTemplateServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingSmsTemplates) return <AppCircularProgress />;
    if (errorLoadingSmsTemplates) {
      return (
        <PageError
          error={errorLoadingSmsTemplates}
          onRetry={() => dispatch(getSmsTemplates())}
        />
      );
    }
    if (!smsTemplates || smsTemplates.length === 0)
      return (
        <Typography sx={{ textAlign: "center", mt: 5 }}>
          No SMS Templates Found
        </Typography>
      );

    return (
      <Grid container spacing={3}>
        {smsTemplates.map((template) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
            <SmsTemplateCard
              template={template}
              onEdit={
                canUpdate
                  ? () => {
                      setSelectedTemplate(template);
                      setIsUpdateModalOpen(true);
                    }
                  : undefined
              }
              onDelete={
                canDelete
                  ? () => {
                      setSelectedTemplate(template);
                      setIsDeleteModalOpen(true);
                    }
                  : undefined
              }
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="SMS Templates"
      subtitle="Manage standard SMS messages."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Template
          </AppButton>
        )
      }
    >
      {renderContent()}
      <CreateSmsTemplateModal
        open={isCreateModalOpen}
        onClose={handleCloseCreate}
        loading={loadingSmsTemplateCreate}
        error={errorCreatingSmsTemplate}
      />
      {selectedTemplate && (
        <UpdateSmsTemplateModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          template={selectedTemplate}
          loading={loadingSmsTemplateUpdate}
          error={errorUpdatingSmsTemplate}
        />
      )}
      {selectedTemplate && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={() => dispatch(deleteSmsTemplate(selectedTemplate.id))}
          title="Delete Template"
          description={`Delete template "${selectedTemplate.templateCode}"?`}
          loading={loadingSmsTemplateDelete}
        />
      )}
    </PageContainer>
  );
};

export default SmsTemplatesPage;
